import React, { useEffect, useRef, useState, useCallback } from 'react';

interface PullToRefreshProps {
  children: React.ReactNode;
  onRefresh: () => Promise<void>;
}

const THRESHOLD = 80;

export default function PullToRefresh({ children, onRefresh }: PullToRefreshProps) {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const startY = useRef<number | null>(null);
  const pulling = useRef(false);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (window.scrollY === 0) {
      startY.current = e.touches[0].clientY;
      pulling.current = true;
    }
  }, []);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!pulling.current || startY.current === null || isRefreshing) return;
    const delta = e.touches[0].clientY - startY.current;
    if (delta > 0 && window.scrollY === 0) {
      e.preventDefault();
      setPullDistance(Math.min(delta * 0.5, THRESHOLD + 20));
    }
  }, [isRefreshing]);

  const handleTouchEnd = useCallback(async () => {
    if (!pulling.current) return;
    pulling.current = false;
    if (pullDistance >= THRESHOLD && !isRefreshing) {
      setIsRefreshing(true);
      setPullDistance(THRESHOLD);
      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
        startY.current = null;
      }
    } else {
      setPullDistance(0);
      startY.current = null;
    }
  }, [pullDistance, isRefreshing, onRefresh]);

  useEffect(() => {
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  const progress = Math.min(pullDistance / THRESHOLD, 1);
  const isTriggered = pullDistance >= THRESHOLD;

  return (
    <div style={{ position: 'relative' }}>
      {/* Pull indicator */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: `${pullDistance}px`,
          transition: isRefreshing ? 'none' : 'height 0.1s ease',
          pointerEvents: 'none',
          overflow: 'hidden',
        }}
      >
        {(pullDistance > 10 || isRefreshing) && (
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              backgroundColor: '#18181b',
              border: '1px solid #27272a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transform: `scale(${0.5 + progress * 0.5})`,
              opacity: progress,
              transition: 'transform 0.1s, opacity 0.1s',
              boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
            }}
          >
            {isRefreshing ? (
              <svg width="18" height="18" viewBox="0 0 18 18" style={{ animation: 'yhuu-spin 0.7s linear infinite' }}>
                <style>{`@keyframes yhuu-spin { to { transform: rotate(360deg); } }`}</style>
                <circle cx="9" cy="9" r="7" fill="none" stroke="#e11d48" strokeWidth="2" strokeDasharray="30 14" strokeLinecap="round" />
              </svg>
            ) : (
              <svg
                width="16" height="16" viewBox="0 0 16 16" fill="none"
                style={{
                  transform: isTriggered ? 'rotate(180deg)' : `rotate(${progress * 180}deg)`,
                  transition: 'transform 0.2s',
                }}
              >
                <path
                  d="M8 3v9M4 8l4 4 4-4"
                  stroke={isTriggered ? '#e11d48' : '#52525b'}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
        )}
      </div>

      {/* Content pushed down while pulling. No transform at rest — a
          transform of any kind (even translateY(0)) turns this div into the
          positioning root for every `position: fixed` descendant in the
          app, which is what was pushing full-screen modals down the page
          instead of centering them on the actual viewport. */}
      <div
        style={pullDistance
          ? { transform: `translateY(${pullDistance}px)`, transition: isRefreshing ? 'none' : 'transform 0.1s ease' }
          : undefined}
      >
        {children}
      </div>
    </div>
  );
}
